import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import {
    tripHeroImageHint,
    validateTripHeroImage,
} from '../../utils/tripImageDimensions';

const ImageUploadField = ({
    field,
    hasError,
    errorMessage,
    imagePreviewUrl,        // set once an attachment actually exists (edit mode / post-creation)
    getImageAttachment,
    handleDeleteAttachment, // still needed for edit-mode "Remove" on an already-uploaded image
    pendingPreviewUrl,      // local objectURL held by the wizard, pre-trip-creation
    onPendingImageSelect,   // (fieldKey, file, previewUrl) => void
    onPendingImageClear,    // (fieldKey) => void
}) => {
    const inputRef = useRef(null);
    const [localError, setLocalError] = useState('');
    const [isValidating, setIsValidating] = useState(false);

    const linkedAttachment = getImageAttachment?.();
    // Priority: a real uploaded attachment > a locally picked, not-yet-uploaded file.
    const displayUrl = pendingPreviewUrl || imagePreviewUrl;

    const validateAndSelect = async (file) => {
        if (!file) return;
        setIsValidating(true);
        try {
            const result = await validateTripHeroImage(file);
            if (!result.ok) {
                setLocalError(result.message);
                return;
            }
            setLocalError('');
            const previewUrl = URL.createObjectURL(file);
            onPendingImageSelect?.(field.key, file, previewUrl);
        } finally {
            setIsValidating(false);
        }
    };

    const handleFileInputChange = (e) => {
        validateAndSelect(e.target.files?.[0]);
        e.target.value = ''; // allow re-picking the same file later
    };

    const handleRemovePhoto = async () => {
        // A locally picked, unsaved file: just discard it
        if (pendingPreviewUrl) {
            onPendingImageClear?.(field.key);
            return;
        }
        if (linkedAttachment) {
            await handleDeleteAttachment(linkedAttachment.id);
        }
    };

    const isHeroVariant = field.variant === 'hero';

    return (
        <Box>
            {displayUrl ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        component="img"
                        src={displayUrl}
                        alt={field.label}
                        sx={{
                            width: isHeroVariant ? '100%' : 72,
                            height: isHeroVariant ? 180 : 72,
                            maxWidth: isHeroVariant ? 480 : 72,
                            objectFit: 'cover',
                            borderRadius: 2,
                        }}
                    />
                    <MDButton
                        onClick={() => inputRef.current?.click()}
                        color="info"
                        variant="outlined"
                        size="small"
                        disabled={isValidating}
                    >
                        Replace Photo
                    </MDButton>
                    <MDButton onClick={handleRemovePhoto} color="error" variant="outlined" size="small">
                        Remove
                    </MDButton>
                </Box>
            ) : (
                <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() => inputRef.current?.click()}
                    disabled={isValidating}
                >
                    {isHeroVariant ? 'Upload Hero Image' : 'Upload Photo'}
                </MDButton>
            )}

            <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                style={{ display: 'none' }}
            />

            <MDTypography variant="caption" color="text" display="block" mt={0.75}>
                {tripHeroImageHint}
            </MDTypography>

            {(hasError || localError) && (
                <MDTypography variant="caption" color="error" display="block" mt={0.5}>
                    {localError || errorMessage || 'This field is required'}
                </MDTypography>
            )}
        </Box>
    );
};

export default ImageUploadField;
